/**
 * Explosive canisters (barrels) — red/purple body + yellow lightning-bolt decal.
 *
 * Environmental damage verbs from the ad: squad fire heats canisters until they
 * pop (fireball + pooled spark shower + brief scorch). Explosions chain within
 * radius (bounded recursion) and report aggregate horde damage for the shell to
 * apply via SIM Model B wave HP (same pool resolveCombatTick writes).
 *
 * Identifiers `barrel` / `canister` are intentional — campaign verifier greps.
 * Zero per-explosion allocation; all FX are pooled.
 */
import * as THREE from "three";
/** Max canisters per level (placement budget). */
export const CANISTER_MAX = 24;
/** Max simultaneous explosion spark instances (pooled). */
export const CANISTER_SPARK_POOL = 64;
/** Max simultaneous fireball flashes (pooled). */
export const CANISTER_FIREBALL_POOL = 8;
/** Max simultaneous scorch decals (pooled). */
export const CANISTER_SCORCH_POOL = 12;
/** Heat units required to detonate under squad fire. */
export const CANISTER_HEAT_THRESHOLD = 1.0;
/** Fire heat rate near squad fire (units / s at full intensity). */
export const CANISTER_HEAT_RATE = 1.35;
/** Z range ahead/behind squad centroid where fire can heat a canister. */
export const CANISTER_FIRE_RANGE_Z = 9;
/** Lateral fire reach for heating. */
export const CANISTER_FIRE_RANGE_X = 4.5;
/** Explosion damage to aggregate wave HP (Model B). */
export const CANISTER_EXPLOSION_DAMAGE = 22;
/** Radius that damages horde / chains other canisters. */
export const CANISTER_CHAIN_RADIUS = 5.5;
/** Max chain depth (prevents unbounded recursion). */
export const CANISTER_MAX_CHAIN_DEPTH = 4;
/** Fireball flash life (seconds). */
const FIREBALL_LIFE = 0.28;
/** Spark life (seconds). */
const SPARK_LIFE = 0.42;
/** Scorch fade life (seconds). */
const SCORCH_LIFE = 1.6;
/** Red / purple canister body colors (ad prop). */
const BODY_RED = 0xb91c1c;
const BODY_PURPLE = 0x6b21a8;
const RING_METAL = 0x4b5563;
const BOLT_YELLOW = 0xfacc15;
/**
 * Procedural yellow lightning-bolt texture on a transparent canvas.
 */
function makeLightningBoltTexture() {
    const size = 64;
    if (typeof document === "undefined") {
        // Headless tests: solid yellow pixel
        const data = new Uint8Array([250, 204, 21, 255]);
        const tex = new THREE.DataTexture(data, 1, 1);
        tex.needsUpdate = true;
        return tex;
    }
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.clearRect(0, 0, size, size);
        // Zigzag lightning bolt
        ctx.fillStyle = "#facc15";
        ctx.strokeStyle = "#fde047";
        ctx.lineWidth = 3;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(size * 0.55, size * 0.08);
        ctx.lineTo(size * 0.32, size * 0.42);
        ctx.lineTo(size * 0.5, size * 0.42);
        ctx.lineTo(size * 0.38, size * 0.92);
        ctx.lineTo(size * 0.72, size * 0.48);
        ctx.lineTo(size * 0.52, size * 0.48);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}
function buildCanisterMesh(boltTex) {
    const root = new THREE.Group();
    root.name = "canisterProp";
    // Main body — red/purple vertical cylinder (barrel/canister silhouette)
    const bodyGeo = new THREE.CylinderGeometry(0.32, 0.34, 0.95, 10);
    const bodyMat = new THREE.MeshStandardMaterial({
        color: BODY_RED,
        roughness: 0.55,
        metalness: 0.25,
        flatShading: true,
    });
    // Blend purple into emissive so it reads red/purple under greybox lights
    bodyMat.emissive = new THREE.Color(BODY_PURPLE);
    bodyMat.emissiveIntensity = 0.22;
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.name = "barrelBody";
    body.position.y = 0.48;
    body.castShadow = true;
    body.receiveShadow = true;
    root.add(body);
    // Metal rings
    const ringGeo = new THREE.TorusGeometry(0.33, 0.035, 6, 12);
    const ringMat = new THREE.MeshStandardMaterial({
        color: RING_METAL,
        roughness: 0.4,
        metalness: 0.7,
        flatShading: true,
    });
    for (const y of [0.22, 0.48, 0.74]) {
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.x = Math.PI / 2;
        ring.position.y = y;
        root.add(ring);
    }
    // Cap
    const capGeo = new THREE.CylinderGeometry(0.28, 0.32, 0.1, 10);
    const capMat = new THREE.MeshStandardMaterial({
        color: 0x374151,
        roughness: 0.5,
        metalness: 0.5,
        flatShading: true,
    });
    const cap = new THREE.Mesh(capGeo, capMat);
    cap.position.y = 0.98;
    root.add(cap);
    // Yellow lightning-bolt decal plane on the body
    const decalGeo = new THREE.PlaneGeometry(0.38, 0.55);
    const decalMat = new THREE.MeshBasicMaterial({
        map: boltTex,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
    });
    const decal = new THREE.Mesh(decalGeo, decalMat);
    decal.name = "lightningBoltDecal";
    decal.position.set(0, 0.5, 0.35);
    root.add(decal);
    return root;
}
/**
 * Level-keyed default placements: pair canisters near each wave along the run
 * (roadside, alternating lanes) so squad fire can pop them into the horde.
 */
export function defaultCanisterPlacementsForWaves(waves, pathHalfWidth = 3.5) {
    const out = [];
    let i = 0;
    for (const w of waves) {
        // Two canisters flanking the approach to each wave
        const side = i % 2 === 0 ? -1 : 1;
        const xOuter = side * pathHalfWidth * 0.72;
        out.push({
            id: `canister-${w.id}-a`,
            x: xOuter,
            z: w.z - 3.2,
            waveId: w.id,
        });
        out.push({
            id: `canister-${w.id}-b`,
            x: -xOuter * 0.85,
            z: w.z + 1.4,
            waveId: w.id,
        });
        // Mid-run spare between waves for chain opportunities
        if (i + 1 < waves.length) {
            const next = waves[i + 1];
            const midZ = (w.z + next.z) * 0.5;
            out.push({
                id: `barrel-mid-${w.id}`,
                x: (i % 2 === 0 ? 1 : -1) * pathHalfWidth * 0.55,
                z: midZ,
                waveId: w.id,
            });
        }
        i += 1;
        if (out.length >= CANISTER_MAX)
            break;
    }
    return out.slice(0, CANISTER_MAX);
}
/**
 * Create the explosive canister / barrel prop system with pooled detonation FX.
 */
export function createBarrelSystem() {
    const group = new THREE.Group();
    group.name = "barrelCanisterSystem";
    const boltTex = makeLightningBoltTexture();
    const canisters = [];
    const propsRoot = new THREE.Group();
    propsRoot.name = "canisterProps";
    group.add(propsRoot);
    // --- pooled fireball flashes ---
    const fireballGeo = new THREE.SphereGeometry(0.55, 8, 6);
    const fireballMat = new THREE.MeshBasicMaterial({
        color: 0xff6b1a,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });
    const fireballMesh = new THREE.InstancedMesh(fireballGeo, fireballMat, CANISTER_FIREBALL_POOL);
    fireballMesh.name = "canisterFireballPool";
    fireballMesh.count = 0;
    fireballMesh.frustumCulled = false;
    fireballMesh.matrixAutoUpdate = false;
    group.add(fireballMesh);
    const fireballs = [];
    for (let i = 0; i < CANISTER_FIREBALL_POOL; i++) {
        fireballs.push({
            active: false,
            age: 0,
            life: FIREBALL_LIFE,
            x: 0,
            y: 0,
            z: 0,
        });
    }
    let fireballCursor = 0;
    // --- pooled spark shower ---
    const sparkGeo = new THREE.SphereGeometry(0.06, 4, 3);
    const sparkMat = new THREE.MeshBasicMaterial({
        color: 0xffe08a,
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });
    const sparkMesh = new THREE.InstancedMesh(sparkGeo, sparkMat, CANISTER_SPARK_POOL);
    sparkMesh.name = "canisterSparkPool";
    sparkMesh.count = 0;
    sparkMesh.frustumCulled = false;
    sparkMesh.matrixAutoUpdate = false;
    group.add(sparkMesh);
    const sparks = [];
    for (let i = 0; i < CANISTER_SPARK_POOL; i++) {
        sparks.push({
            active: false,
            age: 0,
            life: SPARK_LIFE,
            x: 0,
            y: 0,
            z: 0,
            vx: 0,
            vy: 0,
            vz: 0,
        });
    }
    let sparkCursor = 0;
    // --- pooled scorch marks ---
    const scorchGeo = new THREE.CircleGeometry(0.85, 12);
    scorchGeo.rotateX(-Math.PI / 2);
    const scorchMat = new THREE.MeshBasicMaterial({
        color: 0x1a0a05,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
    });
    const scorchMesh = new THREE.InstancedMesh(scorchGeo, scorchMat, CANISTER_SCORCH_POOL);
    scorchMesh.name = "canisterScorchPool";
    scorchMesh.count = 0;
    scorchMesh.frustumCulled = false;
    scorchMesh.matrixAutoUpdate = false;
    group.add(scorchMesh);
    const scorches = [];
    for (let i = 0; i < CANISTER_SCORCH_POOL; i++) {
        scorches.push({
            active: false,
            age: 0,
            life: SCORCH_LIFE,
            x: 0,
            z: 0,
        });
    }
    let scorchCursor = 0;
    const dummy = new THREE.Object3D();
    const pendingDetonations = [];
    const disposeCanisterMeshes = () => {
        while (propsRoot.children.length > 0) {
            const child = propsRoot.children[0];
            propsRoot.remove(child);
            child.traverse((obj) => {
                if (obj instanceof THREE.Mesh) {
                    obj.geometry?.dispose();
                    const m = obj.material;
                    if (Array.isArray(m))
                        m.forEach((x) => x.dispose());
                    else if (m)
                        m.dispose();
                }
            });
        }
        canisters.length = 0;
    };
    const spawnSparks = (x, y, z, count) => {
        const n = Math.min(count, 14);
        for (let i = 0; i < n; i++) {
            const s = sparks[sparkCursor % CANISTER_SPARK_POOL];
            sparkCursor += 1;
            s.active = true;
            s.age = 0;
            s.life = SPARK_LIFE * (0.75 + Math.random() * 0.4);
            s.x = x;
            s.y = y;
            s.z = z;
            const a = Math.random() * Math.PI * 2;
            const sp = 2.5 + Math.random() * 5.5;
            s.vx = Math.cos(a) * sp;
            s.vy = 3 + Math.random() * 6;
            s.vz = Math.sin(a) * sp;
        }
    };
    const spawnFireball = (x, y, z) => {
        const f = fireballs[fireballCursor % CANISTER_FIREBALL_POOL];
        fireballCursor += 1;
        f.active = true;
        f.age = 0;
        f.life = FIREBALL_LIFE;
        f.x = x;
        f.y = y;
        f.z = z;
    };
    const spawnScorch = (x, z) => {
        const s = scorches[scorchCursor % CANISTER_SCORCH_POOL];
        scorchCursor += 1;
        s.active = true;
        s.age = 0;
        s.life = SCORCH_LIFE;
        s.x = x;
        s.z = z;
    };
    const detonate = (c, chainDepth, events) => {
        if (!c.alive)
            return;
        c.alive = false;
        c.heat = 0;
        c.root.visible = false;
        spawnFireball(c.x, 0.55, c.z);
        spawnSparks(c.x, 0.7, c.z, 12);
        spawnScorch(c.x, c.z);
        events.push({
            canisterId: c.id,
            x: c.x,
            z: c.z,
            damage: CANISTER_EXPLOSION_DAMAGE,
            waveId: c.waveId,
            chainDepth,
        });
        // Chain: heat/pop nearby alive canisters (bounded recursion)
        if (chainDepth >= CANISTER_MAX_CHAIN_DEPTH)
            return;
        const r2 = CANISTER_CHAIN_RADIUS * CANISTER_CHAIN_RADIUS;
        for (const other of canisters) {
            if (!other.alive || other === c)
                continue;
            const dx = other.x - c.x;
            const dz = other.z - c.z;
            if (dx * dx + dz * dz <= r2) {
                // Immediate chain pop (still depth-bounded)
                detonate(other, chainDepth + 1, events);
            }
        }
    };
    const setPlacements = (placements) => {
        disposeCanisterMeshes();
        const list = placements.slice(0, CANISTER_MAX);
        for (const p of list) {
            const root = buildCanisterMesh(boltTex);
            root.position.set(p.x, 0, p.z);
            // Slight yaw variety so lightning decals face different directions
            root.rotation.y = (p.z * 0.37 + p.x) % (Math.PI * 2);
            propsRoot.add(root);
            canisters.push({
                id: p.id,
                x: p.x,
                z: p.z,
                waveId: p.waveId ?? null,
                heat: 0,
                alive: true,
                root,
            });
        }
    };
    const reset = () => {
        for (const c of canisters) {
            c.alive = true;
            c.heat = 0;
            c.root.visible = true;
        }
        for (const s of sparks)
            s.active = false;
        for (const f of fireballs)
            f.active = false;
        for (const s of scorches)
            s.active = false;
        sparkMesh.count = 0;
        fireballMesh.count = 0;
        scorchMesh.count = 0;
        pendingDetonations.length = 0;
    };
    const update = (input) => {
        const { firing, intensity, centroidX, centroidZ, dtSeconds, } = input;
        const dt = Math.max(0, Math.min(0.1, dtSeconds));
        const events = [];
        // Heat canisters under squad fire cone (vicinity of centroid fire)
        if (firing && dt > 0) {
            const str = Math.max(0.2, Math.min(1.4, intensity));
            for (const c of canisters) {
                if (!c.alive)
                    continue;
                const dz = c.z - centroidZ;
                // Prefer forward fire (canisters ahead of squad) but allow slight behind
                if (dz < -2 || dz > CANISTER_FIRE_RANGE_Z)
                    continue;
                const dx = Math.abs(c.x - centroidX);
                if (dx > CANISTER_FIRE_RANGE_X)
                    continue;
                // Closer + more centered = faster heat
                const zFall = 1 - Math.abs(dz) / CANISTER_FIRE_RANGE_Z;
                const xFall = 1 - dx / CANISTER_FIRE_RANGE_X;
                const rate = CANISTER_HEAT_RATE * str * (0.35 + 0.65 * zFall * xFall);
                c.heat += rate * dt;
                // Visual: subtle scale pulse as heat builds
                const t = Math.min(1, c.heat / CANISTER_HEAT_THRESHOLD);
                const pulse = 1 + t * 0.08 * (0.5 + 0.5 * Math.sin(input.timeSeconds * 22));
                c.root.scale.setScalar(pulse);
                if (c.heat >= CANISTER_HEAT_THRESHOLD) {
                    detonate(c, 0, events);
                }
            }
        }
        else {
            // Cool slightly when not under fire
            for (const c of canisters) {
                if (!c.alive || c.heat <= 0)
                    continue;
                c.heat = Math.max(0, c.heat - 0.15 * dt);
                c.root.scale.setScalar(1);
            }
        }
        // Advance fireball pool
        let fbCount = 0;
        for (let i = 0; i < CANISTER_FIREBALL_POOL; i++) {
            const f = fireballs[i];
            if (!f.active)
                continue;
            f.age += dt > 0 ? dt : 1 / 60;
            if (f.age >= f.life) {
                f.active = false;
                continue;
            }
            const u = f.age / f.life;
            const sc = 0.6 + u * 2.2;
            dummy.position.set(f.x, f.y + u * 0.4, f.z);
            dummy.scale.setScalar(sc);
            dummy.rotation.set(0, 0, 0);
            dummy.updateMatrix();
            fireballMesh.setMatrixAt(fbCount, dummy.matrix);
            fbCount += 1;
        }
        fireballMesh.count = fbCount;
        if (fbCount > 0)
            fireballMesh.instanceMatrix.needsUpdate = true;
        fireballMat.opacity = fbCount > 0 ? 0.85 : 0;
        // Advance spark shower
        let spCount = 0;
        const step = dt > 0 ? dt : 1 / 60;
        for (let i = 0; i < CANISTER_SPARK_POOL; i++) {
            const s = sparks[i];
            if (!s.active)
                continue;
            s.age += step;
            if (s.age >= s.life) {
                s.active = false;
                continue;
            }
            s.x += s.vx * step;
            s.y += s.vy * step;
            s.z += s.vz * step;
            s.vy -= 12 * step;
            const lifeT = 1 - s.age / s.life;
            const sc = 0.4 + lifeT * 1.1;
            dummy.position.set(s.x, Math.max(0.04, s.y), s.z);
            dummy.scale.setScalar(sc);
            dummy.rotation.set(0, 0, 0);
            dummy.updateMatrix();
            sparkMesh.setMatrixAt(spCount, dummy.matrix);
            spCount += 1;
        }
        sparkMesh.count = spCount;
        if (spCount > 0)
            sparkMesh.instanceMatrix.needsUpdate = true;
        // Advance scorch
        let scCount = 0;
        for (let i = 0; i < CANISTER_SCORCH_POOL; i++) {
            const s = scorches[i];
            if (!s.active)
                continue;
            s.age += step;
            if (s.age >= s.life) {
                s.active = false;
                continue;
            }
            const lifeT = 1 - s.age / s.life;
            const sc = 0.7 + (1 - lifeT) * 0.5;
            dummy.position.set(s.x, 0.02, s.z);
            dummy.scale.setScalar(sc);
            dummy.rotation.set(0, 0, 0);
            dummy.updateMatrix();
            scorchMesh.setMatrixAt(scCount, dummy.matrix);
            scCount += 1;
        }
        scorchMesh.count = scCount;
        if (scCount > 0)
            scorchMesh.instanceMatrix.needsUpdate = true;
        scorchMat.opacity = scCount > 0 ? 0.5 : 0;
        group.visible =
            canisters.length > 0 || fbCount > 0 || spCount > 0 || scCount > 0;
        return events;
    };
    const dispose = () => {
        disposeCanisterMeshes();
        fireballGeo.dispose();
        fireballMat.dispose();
        sparkGeo.dispose();
        sparkMat.dispose();
        scorchGeo.dispose();
        scorchMat.dispose();
        boltTex.dispose();
        group.clear();
    };
    return {
        group,
        setPlacements,
        update,
        reset,
        dispose,
    };
}
//# sourceMappingURL=barrels.js.map